// app/(tabs)/market/boutiques.tsx
//
// Liste des boutiques publiées. Branché sur le vrai backend via
// useShopsList (POST /market, action get-shops).
//
// PATCH V2 : ajout d'une barre de recherche FIXE (toujours visible) sous
// l'en-tête, sur le même principe que market/index.tsx (PATCH V17) : dès
// que searchTerm.trim() n'est pas vide, la liste normale (useShopsList)
// disparaît complètement au profit des résultats de useSearchShops —
// jamais les deux affichés en même temps. Vider le champ (ou taper la
// croix de SearchBar) fait réapparaître la liste normale exactement où
// elle en était (useShopsList garde son propre état, jamais démonté).
// Pas de filtre par catégorie ici : les boutiques n'ont pas de catégorie
// (seuls les produits en ont une) — recherche par nom uniquement.
//
// PATCH V3 : refonte complète du design de carte boutique, pour reprendre
// exactement la structure compacte des cartes produit de market/index.tsx
// (image 78x78 à gauche, infos à droite) plutôt que l'ancienne grande
// carte verticale (image de couverture pleine largeur 140px en haut +
// contenu en dessous). Conservés : badge Premium superposé sur l'image,
// icône Vérifié à côté du nom. Ajouté : un bouton "Visiter" en bas de
// carte (même style actionsRow/actionBtn que index.tsx), qui déclenche la
// même navigation que le tap sur la carte entière — la carte reste
// cliquable dans son ensemble en plus du bouton, pour ne pas réduire la
// zone de tap existante.
//
// PATCH V4 : en-tête refondu à l'identique de market/index.tsx (PATCH
// V18 + V19) — une seule ligne compacte collée en haut de l'écran :
// [icône][nombre][barre de recherche --->]. Suppression du titre
// "Découvrir les boutiques" et du gros headerIconBg (56x56 + icône
// storefront 28px), remplacés par une petite icône storefront (18px) à
// gauche. Le nombre de boutiques ("X boutiques") se déplace au milieu de
// cette ligne, affiché uniquement en mode liste normale (masqué pendant
// une recherche active, comme avant). La SearchBar utilise sa nouvelle
// prop `compact` (voir components/market/SearchBar.tsx) — hauteur ~28px,
// proche de l'icône — et s'étend en flex jusqu'au bord droit de l'écran,
// juste après le nombre. Le paddingTop du scroll et le paddingVertical du
// header sont réduits au minimum pour coller l'ensemble en haut. Aucun
// filtre de catégorie ici (inchangé) : les boutiques n'en ont pas.
//
// PATCH i18n : tous les textes affichés à l'utilisateur passent par
// react-i18next (namespace market.boutiques / market.common), pour
// couvrir fr/en/es/pt. Les données remontées par le backend (nom, ville,
// pays, tagline… — propriété de l'utilisateur qui les a saisies) restent
// non traduites, comme pour toutes les autres sections.

import React, { useState, useRef, useCallback } from 'react';
import {
  StyleSheet,
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  Platform,
  Animated,
  ActivityIndicator,
  RefreshControl,
  Image,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import * as Haptics from 'expo-haptics';
import { useRouter } from 'expo-router';
import { useTranslation } from 'react-i18next';

import { useScrollAutoHide } from '@/hooks/useScrollAutoHide';
import { useTabBarVisibility } from '@/components/CustomTabBar';
import { useShopsList } from '@/hooks/market/useShopsList';
import { useSearchShops } from '@/hooks/market/useMarketSearch';
import SearchBar from '@/components/market/SearchBar';

const NATIVE = Platform.OS !== 'web';

// Utilitaire pour formater les nombres (ex: 1200 -> 1.2k)
const formatNumber = (num: number) => {
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
  if (num >= 1000) return (num / 1000).toFixed(1) + 'k';
  return num.toString();
};

export default function BoutiquesListScreen() {
  const router = useRouter();
  const { t } = useTranslation();

  const [headerVisible, setHeaderVisible] = useState(true);
  const [lastTap, setLastTap] = useState<number | null>(null);
  const headerAnim = useRef(new Animated.Value(0)).current;

  // ── Recherche (PATCH V2) ──
  const [searchTerm, setSearchTerm] = useState('');
  const isSearching = searchTerm.trim().length > 0;

  // Suivi des images de couverture ayant échoué au chargement, par
  // shop_id — même principe que markImageFailed dans index.tsx, pour
  // basculer ces cartes précises en placeholder sans affecter les autres.
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());
  const markImageFailed = useCallback((shopId: string) => {
    setFailedImages(prev => {
      if (prev.has(shopId)) return prev;
      const next = new Set(prev);
      next.add(shopId);
      return next;
    });
  }, []);

  const { setTabBarHidden } = useTabBarVisibility();

  const {
    shops,
    loading,
    loadingMore,
    refreshing,
    error,
    hasMore,
    loadMore,
    refresh,
  } = useShopsList();

  const {
    shops: searchResults,
    loading: searchLoading,
    loadingMore: searchLoadingMore,
    error: searchError,
    hasMore: searchHasMore,
    loadMore: searchLoadMore,
  } = useSearchShops(searchTerm);

  const showHeader = useCallback(() => {
    setHeaderVisible(prevVisible => {
      if (!prevVisible) {
        Animated.spring(headerAnim, {
          toValue: 0,
          useNativeDriver: true,
          tension: 80,
          friction: 10,
        }).start();
      }
      return true;
    });
  }, [headerAnim]);

  const hideHeader = useCallback(() => {
    setHeaderVisible(prevVisible => {
      if (prevVisible) {
        Animated.spring(headerAnim, {
          toValue: -100,
          useNativeDriver: true,
          tension: 80,
          friction: 10,
        }).start();
      }
      return false;
    });
  }, [headerAnim]);

  const { handleScroll } = useScrollAutoHide({
    onShow: useCallback(() => {
      showHeader();
      setTabBarHidden(false);
    }, [showHeader, setTabBarHidden]),
    onHide: useCallback(() => {
      hideHeader();
      setTabBarHidden(true);
    }, [hideHeader, setTabBarHidden]),
    onScrollExtra: useCallback((e: any) => {
      const { contentOffset, contentSize, layoutMeasurement } = e.nativeEvent;
      const ratio = (contentOffset.y + layoutMeasurement.height) / contentSize.height;
      if (ratio > 0.85) {
        // Pagine la liste actuellement affichée (recherche ou liste
        // normale), jamais les deux en même temps.
        if (isSearching) {
          if (searchHasMore && !searchLoadingMore) searchLoadMore();
        } else if (hasMore && !loadingMore) {
          loadMore();
        }
      }
    }, [isSearching, searchHasMore, searchLoadingMore, searchLoadMore, hasMore, loadingMore, loadMore]),
  });

  const toggleHeader = useCallback(() => {
    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    if (headerVisible) {
      hideHeader();
      setTabBarHidden(true);
    } else {
      showHeader();
      setTabBarHidden(false);
    }
  }, [headerVisible, hideHeader, showHeader, setTabBarHidden]);

  const handleDoubleTap = useCallback(() => {
    const now = Date.now();
    if (lastTap && now - lastTap < 300) toggleHeader();
    else setLastTap(now);
  }, [lastTap, toggleHeader]);

  const handleShopPress = useCallback((slug: string) => {
    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    router.push(`/(tabs)/market/boutiques/${slug}`);
  }, [router]);

  // ── Choix de la liste à afficher (recherche ou liste normale) ──
  const displayedShops       = isSearching ? searchResults     : shops;
  const displayedLoading     = isSearching ? searchLoading     : loading;
  const displayedLoadingMore = isSearching ? searchLoadingMore : loadingMore;
  const displayedError       = isSearching ? searchError       : error;

  // Rendu d'une carte boutique — factorisé pour être utilisé identiquement
  // par la liste normale et par les résultats de recherche. PATCH V3 :
  // structure compacte reprise de renderProductCard dans index.tsx
  // (image 78x78 + infos à droite), avec un bouton "Visiter" en bas.
  const renderShopCard = (shop: typeof shops[number]) => {
    const location = [shop.city, shop.country].filter(Boolean).join(', ');
    const showCover = !!shop.cover_image_url && !failedImages.has(shop.shop_id);

    return (
      <TouchableOpacity
        key={shop.shop_id}
        style={styles.shopCard}
        activeOpacity={0.9}
        onPress={() => handleShopPress(shop.slug)}
      >
        <View style={styles.shopTopRow}>
          {showCover ? (
            <View style={styles.shopImageWrap}>
              <Image
                source={{ uri: shop.cover_image_url }}
                style={styles.shopImage}
                resizeMode="cover"
                onError={() => markImageFailed(shop.shop_id)}
              />
              {shop.is_premium && (
                <View style={styles.premiumBadge}>
                  <Ionicons name="star" size={9} color="#FFF" />
                </View>
              )}
            </View>
          ) : (
            <View style={styles.shopImagePlaceholder}>
              <Ionicons name="storefront-outline" size={28} color="#C4B5FD" />
              {shop.is_premium && (
                <View style={styles.premiumBadge}>
                  <Ionicons name="star" size={9} color="#FFF" />
                </View>
              )}
            </View>
          )}

          <View style={styles.shopInfo}>
            <View style={styles.titleRow}>
              <Text style={styles.shopName} numberOfLines={1}>{shop.name}</Text>
              {shop.is_verified && (
                <Ionicons name="checkmark-circle" size={15} color="#3B82F6" style={styles.verifiedIcon} />
              )}
            </View>
            {!!shop.tagline && (
              <Text style={styles.shopTagline} numberOfLines={1}>{shop.tagline}</Text>
            )}
            <View style={styles.metaRow}>
              {location ? (
                <Text style={styles.metaText} numberOfLines={1}>{location}</Text>
              ) : (
                <Text style={styles.metaText}>{t('market.boutiques.online')}</Text>
              )}
              <View style={styles.dotSeparator} />
              <Text style={styles.metaText}>
                {t('market.boutiques.productsCount', { count: shop.total_products })}
              </Text>
              <View style={styles.dotSeparator} />
              <Text style={styles.metaText}>
                {t('market.boutiques.subscribersCount', { count: shop.subscriber_count })}
              </Text>
            </View>
          </View>
        </View>

        {/* Bouton Visiter — même style que actionBtn/actionsRow de
            index.tsx. Zone de tap indépendante de la carte, mais déclenche
            la même navigation (handleShopPress). */}
        <View style={styles.actionsRow}>
          <TouchableOpacity
            style={[styles.actionBtn, styles.actionBtnSecondary]}
            activeOpacity={0.7}
            onPress={() => handleShopPress(shop.slug)}
          >
            <Ionicons name="storefront-outline" size={14} color="#6610B1" />
            <Text style={styles.actionBtnText}>{t('market.boutiques.visit')}</Text>
          </TouchableOpacity>
        </View>
      </TouchableOpacity>
    );
  };

  return (
    <View style={styles.container}>
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
        onScroll={handleScroll}
        scrollEventThrottle={16}
        onTouchEnd={handleDoubleTap}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={refresh} tintColor="#6610B1" />
        }
      >
        {/* ── En-tête compact (PATCH V4, identique à index.tsx V18/V19) :
            icône + nombre + recherche, tous sur une seule ligne collée en
            haut. Titre "Découvrir les boutiques" supprimé. Le nombre de
            boutiques n'est affiché qu'en mode liste normale (masqué
            pendant une recherche active). */}
        <View style={styles.headerBlock}>
          <Ionicons name="storefront-outline" size={18} color="#6610B1" />

          {!displayedLoading && !displayedError && !isSearching && (
            <Text style={styles.headerCount} numberOfLines={1}>
              {t('market.boutiques.headerCount', { count: shops.length })}
            </Text>
          )}

          <View style={styles.headerSearchWrap}>
            <SearchBar
              value={searchTerm}
              onChangeText={setSearchTerm}
              placeholder={t('market.boutiques.searchPlaceholder')}
              compact
            />
          </View>
        </View>

        {/* ── Chargement initial ── */}
        {displayedLoading && (
          <View style={styles.centerBox}>
            <ActivityIndicator size="small" color="#6610B1" />
            <Text style={styles.centerBoxText}>
              {isSearching ? t('market.boutiques.loadingSearch') : t('market.boutiques.loadingNormal')}
            </Text>
          </View>
        )}

        {/* ── Erreur ── */}
        {!displayedLoading && displayedError && (
          <View style={styles.centerBox}>
            <Ionicons name="cloud-offline-outline" size={40} color="#C4B5FD" />
            <Text style={styles.centerBoxText}>
              {displayedError === 'session_expired'
                ? t('market.common.sessionExpiredReconnect2')
                : isSearching
                  ? t('market.boutiques.errorSearch')
                  : t('market.boutiques.errorNormal')}
            </Text>
            {!isSearching && (
              <TouchableOpacity style={styles.retryBtn} onPress={refresh} activeOpacity={0.8}>
                <Text style={styles.retryBtnText}>{t('market.common.retry')}</Text>
              </TouchableOpacity>
            )}
          </View>
        )}

        {/* ── Liste vide ── */}
        {!displayedLoading && !displayedError && displayedShops.length === 0 && (
          <View style={styles.centerBox}>
            <Ionicons name="search-outline" size={40} color="#C4B5FD" />
            <Text style={styles.centerBoxText}>
              {isSearching ? t('market.boutiques.emptySearch') : t('market.boutiques.emptyNormal')}
            </Text>
          </View>
        )}

        {/* ── Liste des boutiques (recherche ou liste normale) ── */}
        {!displayedLoading && displayedShops.map(renderShopCard)}

        {/* ── Pagination en cours ── */}
        {displayedLoadingMore && (
          <View style={styles.loadMoreBox}>
            <ActivityIndicator size="small" color="#6610B1" />
          </View>
        )}

        <View style={{ height: 40 }} />
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F0F2F5',
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: 14,
    paddingTop: 6,
  },
  headerBlock: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 4,
    marginBottom: 12,
  },
  headerCount: {
    fontSize: 12.5,
    fontWeight: '600',
    color: '#666',
    flexShrink: 0,
  },
  headerSearchWrap: {
    flex: 1,
  },
  centerBox: {
    alignItems: 'center',
    paddingVertical: 40,
    paddingHorizontal: 24,
    gap: 10,
  },
  centerBoxText: {
    fontSize: 13,
    color: '#999',
    textAlign: 'center',
  },
  retryBtn: {
    marginTop: 4,
    paddingHorizontal: 18,
    paddingVertical: 9,
    backgroundColor: '#F3E8FF',
    borderRadius: 10,
  },
  retryBtnText: {
    fontSize: 13,
    fontWeight: '700',
    color: '#6610B1',
  },
  loadMoreBox: {
    paddingVertical: 16,
    alignItems: 'center',
  },

  /* --- Cartes Boutiques (PATCH V3 : style compact repris de index.tsx) --- */
  shopCard: {
    backgroundColor: '#fff',
    borderRadius: 14,
    padding: 12,
    marginBottom: 10,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.06,
    shadowRadius: 6,
    elevation: 2,
  },
  shopTopRow: {
    flexDirection: 'row',
  },
  shopImageWrap: {
    width: 78,
    height: 78,
    borderRadius: 10,
    marginRight: 12,
    position: 'relative',
    overflow: 'hidden',
  },
  shopImage: {
    width: '100%',
    height: '100%',
    backgroundColor: '#F3E8FF',
  },
  shopImagePlaceholder: {
    width: 78,
    height: 78,
    borderRadius: 10,
    backgroundColor: '#F3E8FF',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
    position: 'relative',
  },
  premiumBadge: {
    position: 'absolute',
    top: 4,
    right: 4,
    width: 18,
    height: 18,
    borderRadius: 9,
    backgroundColor: '#F59E0B',
    justifyContent: 'center',
    alignItems: 'center',
  },
  shopInfo: {
    flex: 1,
    justifyContent: 'center',
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  shopName: {
    fontSize: 14.5,
    fontWeight: '700',
    color: '#222',
    flexShrink: 1,
  },
  verifiedIcon: {
    marginLeft: 4,
  },
  shopTagline: {
    fontSize: 12,
    color: '#666',
    marginTop: 2,
  },
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 5,
    flexWrap: 'wrap',
  },
  metaText: {
    fontSize: 11.5,
    color: '#999',
  },
  dotSeparator: {
    width: 3,
    height: 3,
    borderRadius: 1.5,
    backgroundColor: '#D1D5DB',
    marginHorizontal: 6,
  },
  actionsRow: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 8,
  },
  actionBtn: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    backgroundColor: '#F9F5FF',
    paddingHorizontal: 8,
    paddingVertical: 7,
    borderRadius: 8,
  },
  actionBtnSecondary: {
    backgroundColor: '#F3F0FF',
  },
  actionBtnText: {
    fontSize: 11,
    fontWeight: '600',
    color: '#333',
  },
});
